import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import ProjectManagement from './pages/project-management';
import BidManagement from './pages/bid-management';
import SimpleProposalPage from './pages/bid-management/SimpleProposalPage';
import PhasedProjectProposalPage from './pages/bid-management/PhasedProjectProposalPage';
import DetailedProposalPage from './pages/bid-management/DetailedProposalPage';
import BidLetterPage from './pages/bid-management/BidLetterPage';
import ProjectPhases from './pages/project-phases';
import Dashboard from './pages/dashboard';
import ResourceManagement from './pages/resource-management';
import CustomerManagement from './pages/customer-management';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import Profile from './pages/auth/Profile';
import TeamAdministration from './pages/team-administration';
import ProjectDetailManagement from './pages/project-detail-management';
import Portfolio from './pages/portfolio';
import AuthCallback from './pages/auth/AuthCallback';
import Analytics from './pages/analytics';
import InvitationAcceptance from './pages/invitation-acceptance';
import ProfileSetup from './pages/profile-setup';
import AuditLog from './pages/audit-log';
import ApplicationBugTracker from './pages/application-bug-tracker';
import ForceChangePassword from 'pages/auth/ForceChangePassword';
import PMDashboard from './pages/pm-dashboard';
import PrivacyPolicy from './pages/privacy-policy';
import LegalTerms from './pages/legal-terms';
import CookiePolicy from './pages/cookie-policy';
import LegalContentManagement from './pages/legal-content-management';
import ProspectManagement from './pages/prospect-management';
import ProspectDetailView from './pages/prospect-detail-view';
import ProspectAnalyticsDashboard from './pages/prospect-analytics-dashboard';
import PWAInstallInstructions from './pages/pwa-install-instructions';
import MobileAppConfiguration from './pages/mobile-app-configuration';
import MobileDashboard from './pages/mobile-dashboard';
import MobileCustomerManagement from './pages/mobile-customer-management';
import MobileBidManagement from './pages/mobile-bid-management';
import MobileBottomNavigation from './pages/mobile-bottom-navigation';
import UserProfile from './pages/user-profile';
import AppIcon from './components/AppIcon';
import Archive from './pages/archive';
import ProjectEdit from './pages/project-edit';
import SubContractors from './pages/sub-contractors';
import Vendors from './pages/vendors';
import DisplayPage from './pages/display';
import DisplayControlPage from './pages/display/control';
import SlideManagement from './pages/slide-management';

// ─── Mobile detection helper ──────────────────────────────────────────────────
const isMobileDevice = () => {
  const ua = navigator?.userAgent?.toLowerCase() || '';
  return /iphone|ipad|ipod|android|mobile|tablet/?.test(ua);
};

// ─── Safe error normalizer ────────────────────────────────────────────────────
function toSafeError(error) {
  if (error instanceof Error) return error;
  const msg =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object'
      ? error?.message || JSON.stringify(error)
      : 'Unknown error';
  const safe = new Error(msg);
  if (error && typeof error === 'object') {
    try { Object.assign(safe, error); } catch (_) {}
  }
  return safe;
}

// ─── Per-page error boundary ──────────────────────────────────────────────────
// Catches errors at the individual page level so they never bubble up to the
// platform's rocket-web.js interceptor as undefined/non-Error values.
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const safeError = toSafeError(error);
    try { safeError.__ErrorBoundary = true; } catch (_) {}
    window.__COMPONENT_ERROR__?.(safeError, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
          <div className="text-center p-8 max-w-md">
            <div className="flex justify-center items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="42px" height="42px" viewBox="0 0 32 33" fill="none">
                <path d="M16 28.5C22.6274 28.5 28 23.1274 28 16.5C28 9.87258 22.6274 4.5 16 4.5C9.37258 4.5 4 9.87258 4 16.5C4 23.1274 9.37258 28.5 16 28.5Z" stroke="#343330" strokeWidth="2" strokeMiterlimit="10" />
                <path d="M11.5 15.5C12.3284 15.5 13 14.8284 13 14C13 13.1716 12.3284 12.5 11.5 12.5C10.6716 12.5 10 13.1716 10 14C10 14.8284 10.6716 15.5 11.5 15.5Z" fill="#343330" />
                <path d="M20.5 15.5C21.3284 15.5 22 14.8284 22 14C22 13.1716 21.3284 12.5 20.5 12.5C19.6716 12.5 19 13.1716 19 14C19 14.8284 19.6716 15.5 20.5 15.5Z" fill="#343330" />
                <path d="M21 22.5C19.9625 20.7062 18.2213 19.5 16 19.5C13.7787 19.5 12.0375 20.7062 11 22.5" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 text-center mb-6">
              <h1 className="text-2xl font-medium text-neutral-800">Something went wrong</h1>
              <p className="text-neutral-600 text-base w-8/12 mx-auto">
                We encountered an unexpected error. You can try again or return to the dashboard.
              </p>
            </div>
            <div className="flex justify-center items-center gap-3">
              <button
                onClick={this.handleReset}
                className="bg-neutral-200 hover:bg-neutral-300 text-neutral-800 font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm"
              >
                <AppIcon name="ArrowLeft" size={18} color="#fff" />
                Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// Helper: wraps any element in a PageErrorBoundary
const PEB = ({ children }) => <PageErrorBoundary>{children}</PageErrorBoundary>;

const Routes = () => {
  const mobileHome = isMobileDevice() ? '/project-management' : '/dashboard';

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/auth/signin" element={<PEB><SignIn /></PEB>} />
        <Route path="/auth/signup" element={<PEB><SignUp /></PEB>} />
        <Route path="/auth/forgot-password" element={<PEB><ForgotPassword /></PEB>} />
        <Route path="/auth/force-change-password" element={<PEB><ForceChangePassword /></PEB>} />
        <Route path="/auth/callback" element={<PEB><AuthCallback /></PEB>} />
        <Route path="/invitation-acceptance" element={<PEB><InvitationAcceptance /></PEB>} />

        {/* Protected Routes */}
        <Route path="/" element={<PEB><ProtectedRoute><Navigate to={mobileHome} replace /></ProtectedRoute></PEB>} />
        <Route path="/dashboard" element={<PEB><ProtectedRoute><Dashboard /></ProtectedRoute></PEB>} />
        <Route path="/profile" element={<PEB><ProtectedRoute><Profile /></ProtectedRoute></PEB>} />
        <Route path="/profile-setup" element={<PEB><ProtectedRoute><ProfileSetup /></ProtectedRoute></PEB>} />
        <Route path="/project-management" element={<PEB><ProtectedRoute><ProjectManagement /></ProtectedRoute></PEB>} />
        <Route path="/portfolio" element={<PEB><ProtectedRoute><Portfolio /></ProtectedRoute></PEB>} />
        <Route path="/project-detail-management/:id" element={<PEB><ProtectedRoute><ProjectDetailManagement /></ProtectedRoute></PEB>} />
        <Route path="/bid-management" element={<PEB><ProtectedRoute><BidManagement /></ProtectedRoute></PEB>} />
        <Route path="/bid-management/simple-proposal" element={<PEB><ProtectedRoute><SimpleProposalPage /></ProtectedRoute></PEB>} />
        <Route path="/bid-management/phased-proposal" element={<PEB><ProtectedRoute><PhasedProjectProposalPage /></ProtectedRoute></PEB>} />
        <Route path="/bid-management/detailed-proposal" element={<PEB><ProtectedRoute><DetailedProposalPage /></ProtectedRoute></PEB>} />
        <Route path="/bid-management/letter" element={<PEB><ProtectedRoute><BidLetterPage /></ProtectedRoute></PEB>} />
        <Route path="/project-phases" element={<PEB><ProtectedRoute><ProjectPhases /></ProtectedRoute></PEB>} />
        <Route path="/resource-management" element={<PEB><ProtectedRoute><ResourceManagement /></ProtectedRoute></PEB>} />
        <Route path="/customer-management" element={<PEB><ProtectedRoute><CustomerManagement /></ProtectedRoute></PEB>} />
        <Route path="/prospect-management" element={<PEB><ProtectedRoute><ProspectManagement /></ProtectedRoute></PEB>} />
        <Route path="/prospect-detail-view" element={<PEB><ProtectedRoute><ProspectDetailView /></ProtectedRoute></PEB>} />
        <Route path="/prospect-analytics-dashboard" element={<PEB><ProtectedRoute><ProspectAnalyticsDashboard /></ProtectedRoute></PEB>} />
        <Route path="/team-administration" element={<PEB><ProtectedRoute><TeamAdministration /></ProtectedRoute></PEB>} />
        <Route path="/audit-log" element={<PEB><ProtectedRoute><AuditLog /></ProtectedRoute></PEB>} />
        <Route path="/analytics" element={<PEB><ProtectedRoute><Analytics /></ProtectedRoute></PEB>} />
        <Route path="/application-bug-tracker" element={<PEB><ProtectedRoute><ApplicationBugTracker /></ProtectedRoute></PEB>} />
        <Route path="/pm-dashboard" element={<PEB><ProtectedRoute><PMDashboard /></ProtectedRoute></PEB>} />
        <Route path="/project-reports" element={<Navigate to="/analytics" replace />} />
        <Route path="/pwa-install-instructions" element={<PEB><ProtectedRoute><PWAInstallInstructions /></ProtectedRoute></PEB>} />
        <Route path="/mobile-app-configuration" element={<PEB><ProtectedRoute><MobileAppConfiguration /></ProtectedRoute></PEB>} />
        <Route path="/mobile-dashboard" element={<PEB><ProtectedRoute><MobileDashboard /></ProtectedRoute></PEB>} />
        <Route path="/mobile-customer-management" element={<PEB><ProtectedRoute><MobileCustomerManagement /></ProtectedRoute></PEB>} />
        <Route path="/mobile-bid-management" element={<PEB><ProtectedRoute><MobileBidManagement /></ProtectedRoute></PEB>} />
        <Route path="/mobile-bottom-navigation" element={<PEB><ProtectedRoute><MobileBottomNavigation /></ProtectedRoute></PEB>} />
        <Route path="/archive" element={<PEB><ProtectedRoute><Archive /></ProtectedRoute></PEB>} />
        <Route path="/project-edit/:id" element={<PEB><ProtectedRoute><ProjectEdit /></ProtectedRoute></PEB>} />
        <Route path="/user-profile" element={<PEB><ProtectedRoute><UserProfile /></ProtectedRoute></PEB>} />
        <Route path="/sub-contractors" element={<PEB><ProtectedRoute><SubContractors /></ProtectedRoute></PEB>} />
        <Route path="/vendors" element={<PEB><ProtectedRoute><Vendors /></ProtectedRoute></PEB>} />

        {/* Legal Pages - Public */}
        <Route path="/privacy-policy" element={<PEB><PrivacyPolicy /></PEB>} />
        <Route path="/legal-terms" element={<PEB><LegalTerms /></PEB>} />
        <Route path="/cookie-policy" element={<PEB><CookiePolicy /></PEB>} />
        <Route path="/legal-content-management" element={<PEB><ProtectedRoute><LegalContentManagement /></ProtectedRoute></PEB>} />

        {/* Public Display - no auth required */}
        <Route path="/display" element={<PEB><DisplayPage /></PEB>} />
        <Route path="/display/control" element={<PEB><DisplayControlPage /></PEB>} />

        {/* Slide Management - admin only */}
        <Route path="/slide-management" element={<PEB><ProtectedRoute><SlideManagement /></ProtectedRoute></PEB>} />

        {/* Catch all */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
